/**
 * Language Switcher component
 *
 * Reusable language selector used in both UserMenu (authenticated)
 * and LoginPage (pre-authentication).
 */

import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import { LANGUAGE_STORAGE_KEY } from '../config'
import { updateMySettings } from '../../services/api'
import { tokenStorage } from '../../services/api'

interface LanguageOption {
  code: string
  nativeName: string
}

const LANGUAGES: LanguageOption[] = [
  { code: 'en', nativeName: 'English' },
  { code: 'de', nativeName: 'Deutsch' },
]

interface LanguageSwitcherProps {
  /** 'menu' for use within UserMenu, 'standalone' for LoginPage */
  variant: 'menu' | 'standalone'
  /** Whether the user is authenticated (controls database persistence) */
  isAuthenticated?: boolean
}

export function LanguageSwitcher({
  variant,
  isAuthenticated = false,
}: LanguageSwitcherProps): React.ReactElement {
  const { i18n } = useTranslation()

  const handleLanguageChange = (langCode: string): void => {
    if (langCode === i18n.language) return

    // 1. Immediate UI update
    void i18n.changeLanguage(langCode)

    // 2. Persist to localStorage
    localStorage.setItem(LANGUAGE_STORAGE_KEY, langCode)

    // 3. Persist to database if authenticated
    if (isAuthenticated && tokenStorage.hasTokens()) {
      void updateMySettings({ language: langCode })
    }
  }

  if (variant === 'standalone') {
    return (
      <div className="flex items-center justify-center gap-2">
        <Globe className="h-4 w-4 text-slate-400" />
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => {
              handleLanguageChange(lang.code)
            }}
            className={`rounded-lg px-2 py-1 text-sm transition-colors ${
              i18n.language === lang.code
                ? 'bg-[#4e5e82]/10 font-medium text-[#4e5e82] dark:bg-[#8a9bba]/15 dark:text-[#8a9bba]'
                : 'text-slate-500 hover:bg-lime-50 hover:text-lime-700 dark:text-slate-400 dark:hover:bg-lime-900/20 dark:hover:text-lime-300'
            }`}
          >
            {lang.nativeName}
          </button>
        ))}
      </div>
    )
  }

  // variant === 'menu'
  return (
    <div className="px-3 py-2">
      <div className="mb-1 flex items-center gap-2">
        <Globe className="h-3.5 w-3.5 text-slate-400" />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {i18n.t('settings:language.label')}
        </span>
      </div>
      <div className="flex gap-1">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => {
              handleLanguageChange(lang.code)
            }}
            className={`flex-1 rounded-md px-2 py-1 text-xs transition-colors ${
              i18n.language === lang.code
                ? 'bg-[#4e5e82]/10 font-medium text-[#4e5e82] dark:bg-[#8a9bba]/15 dark:text-[#8a9bba]'
                : 'text-slate-500 hover:bg-lime-50 hover:text-lime-700 dark:text-slate-400 dark:hover:bg-lime-900/20 dark:hover:text-lime-300'
            }`}
          >
            {lang.nativeName}
          </button>
        ))}
      </div>
    </div>
  )
}
